import React, { lazy, Suspense, useContext, useMemo } from 'react';
import { Switch, Redirect, useLocation } from 'react-router-dom';
import { NexusProvider } from '@return-rabbit/nexus';
import '@return-rabbit/nexus/build/index.css';
import { Helmet } from 'react-helmet';
import _get from 'lodash/get';

import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import * as RouteGuard from 'components/RouteGuards';
import { AuthContext } from 'context/AuthContext';
import useLogRocket from 'utils/hooks/useLogRocket';
import useNexusConfig from 'utils/hooks/useNexusConfig';
import useSegment from 'utils/hooks/useSegment';
import useStore from 'utils/hooks/useStore';
import { ROUTES } from 'dictionary/routes';

const Login = lazy(() => import('views/Login'));
const SignIn = lazy(() => import('views/SignIn'));
const Logout = lazy(() => import('views/Logout'));
const GiftReturnLogin = lazy(() => import('views/GiftReturnLogin'));
const QrShippingLabel = lazy(() => import('views/QrShippingLabel'));
const DropoffLocations = lazy(() => import('views/DropoffLocations'));
const ShipmentTracking = lazy(() => import('views/ShipmentTracking'));
const Account = lazy(() => import('views/Account'));
const OrderDetails = lazy(() => import('views/Orders/OrderDetails'));
const OrderService = lazy(() => import('views/OrderService'));
const ErrorPage = lazy(() => import('views/Errors/ErrorPage'));
const NotFound = lazy(() => import('views/Errors/NotFound'));
const PortalDisabled = lazy(() => import('views/Errors/PortalDisabled'));
const RequestDetails = lazy(() => import('views/Requests/RequestDetails'));

const NEXUS_SENTRY_DSN = process.env.REACT_APP_NEXUS_SENTRY_APP_ID;

export default function App() {
  const { authToken, userData, getStoreDetails, masterData, storeProperties } =
    useContext(AuthContext);
  const { getStoreProperty } = useStore();
  const location = useLocation();

  const segment = useSegment();
  const logRocket = useLogRocket();
  const { axiosConfig } = useNexusConfig();

  const isPortalDisabled =
    getStoreProperty('customer_portal_active', true) === false;

  const storeInfo = getStoreDetails();
  const storeName = _get(storeInfo, 'name', '');
  const favicon = _get(storeInfo, 'favicon', '');

  const userInfo = useMemo(() => {
    if (!userData) return null;

    return {
      id: _get(userData, 'userId'),
      email: _get(userData, 'email'),
      firstName: _get(userData, 'firstName'),
      lastName: _get(userData, 'lastName'),
    };
  }, [userData]);

  const analytics = useMemo(() => {
    return {
      segment,
      logRocket,
    };
  }, [segment, logRocket]);

  return (
    <ErrorBoundary resetKey={location.key}>
      <Helmet>
        <title>{storeName ? `${storeName} Support` : 'Returns Portal'}</title>
        {favicon && <link rel="icon" href={favicon} />}
      </Helmet>
      <NexusProvider
        key={authToken}
        mode="SHOPPER"
        axiosConfig={axiosConfig}
        masterData={masterData}
        storeProperties={storeProperties}
        userInfo={userInfo}
        analytics={analytics}
        sentryDsn={NEXUS_SENTRY_DSN}
      >
        {/* Route-scoped: a failed lazy chunk here degrades to this one
            route's fallback instead of the whole shell above catching it. */}
        <ErrorBoundary resetKey={location.key}>
          <Suspense fallback={<Loader />}>
            <Switch>
              <RouteGuard.Public exact path={ROUTES.DISABLED}>
                {isPortalDisabled ? (
                  <PortalDisabled />
                ) : (
                  <Redirect to={ROUTES.ROOT} />
                )}
              </RouteGuard.Public>
              {isPortalDisabled && <Redirect to={ROUTES.DISABLED} />}

              <RouteGuard.Auth path={ROUTES.LOGIN}>
                <Login />
              </RouteGuard.Auth>
              <RouteGuard.Auth path={ROUTES.GIFT_LOGIN}>
                <GiftReturnLogin />
              </RouteGuard.Auth>

              <RouteGuard.Public path={ROUTES.SIGN_IN}>
                <SignIn />
              </RouteGuard.Public>
              <RouteGuard.Public path={ROUTES.LOGOUT}>
                <Logout />
              </RouteGuard.Public>
              <RouteGuard.Public path={ROUTES.ERROR}>
                <ErrorPage />
              </RouteGuard.Public>
              <RouteGuard.Public path={ROUTES.NOT_FOUND}>
                <NotFound />
              </RouteGuard.Public>
              <RouteGuard.Public path={ROUTES.TRACKING.DETAILS}>
                <ShipmentTracking />
              </RouteGuard.Public>
              <RouteGuard.Public path={ROUTES.QR_SHIPPING_LABEL}>
                <QrShippingLabel />
              </RouteGuard.Public>
              <RouteGuard.Public path={ROUTES.DROPOFF_LOCATIONS}>
                <DropoffLocations />
              </RouteGuard.Public>

              <RouteGuard.Protected path={ROUTES.ACCOUNT.RETURNS_DETAILS}>
                <RequestDetails />
              </RouteGuard.Protected>
              <RouteGuard.Protected path={ROUTES.ORDER_DETAILS}>
                <OrderDetails />
              </RouteGuard.Protected>
              <RouteGuard.Protected path={ROUTES.REQUEST.ROOT}>
                <OrderService />
              </RouteGuard.Protected>
              <RouteGuard.Protected path={ROUTES.ACCOUNT.ROOT}>
                <Account />
              </RouteGuard.Protected>
              <RouteGuard.Protected path={ROUTES.ROOT}>
                <Redirect to={ROUTES.ACCOUNT.ROOT} />
              </RouteGuard.Protected>
            </Switch>
          </Suspense>
        </ErrorBoundary>
      </NexusProvider>
    </ErrorBoundary>
  );
}
